import React, { FunctionComponent } from "react";
import { styled } from "@mui/material/styles";
import _ from "lodash";

import Box from "@mui/material/Box";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import MoreVertIcon from "@mui/icons-material/MoreVert";

import { grey } from "designSystem/styles/color";
import { cursorForceColResize, cursorForceRowResize } from "designSystem/styles/css";
import { HOVER } from "designSystem/theme";

const PREFIX = "ResizeHandle";
const handleSize = 8;
let dragging = false;
let handle: HTMLElement;
let startPosition: number;

const classes = {
	root: `${PREFIX}root`,
	horiz: `${PREFIX}horiz`,
	icon: `${PREFIX}icon`,
	vert: `${PREFIX}vert`,
};

const StyledBox = styled(Box)({
	[`&.${classes.root}`]: {
		alignItems: "center",
		backgroundColor: grey[200],
		color: grey.A100,
		display: "flex",
		justifyContent: "center",

		[HOVER]: {
			backgroundColor: grey[400],
		},
	},
	[`&.${classes.horiz}`]: {
		cursor: "row-resize",
		height: handleSize,
	},
	[`& .${classes.icon}`]: {
		fontSize: 20,
	},
	[`&.${classes.vert}`]: {
		cursor: "col-resize",
		height: "100%",
		width: handleSize,
	},
});

export interface ResizeHandleProps {
	/** indicates if resize handle is horizontal (default is vertical) */
	horizontal?: boolean;

	/**
	 * callback when handle is moved (dragging is true) and finished (dragging is false);
	 * delta is difference from start of drag rather than difference from last move
	 */
	onResize: (delta: number, dragging: boolean) => void;

	/** throttle duration (default 25ms) */
	throttleDuration?: number;
}

export const ResizeHandle: FunctionComponent<ResizeHandleProps> = (props) => {
	const { horizontal, onResize, throttleDuration } = props;

	const xy = horizontal ? "clientY" : "clientX";
	const cursorCss = horizontal ? cursorForceRowResize : cursorForceColResize;
	const Icon = horizontal ? MoreHorizIcon : MoreVertIcon;

	const onPointerMove = _.throttle((event: PointerEvent) => {
		dragging = true;
		onResize(event[xy] - startPosition, dragging);
	}, throttleDuration || 25);

	const onPointerUp = (event: PointerEvent) => {
		window.removeEventListener("pointermove", onPointerMove, true);
		window.removeEventListener("pointerup", onPointerUp, true);
		document.body.classList.remove(cursorCss);
		handle.style.backgroundColor = null;
		dragging = false;
		onResize(event[xy] - startPosition, dragging);
	};

	const onPointerDown = (event: React.PointerEvent<HTMLElement>) => {
		window.addEventListener("pointermove", onPointerMove, true);
		window.addEventListener("pointerup", onPointerUp, true);
		document.body.classList.add(cursorCss);
		handle = event.currentTarget;
		handle.style.backgroundColor = grey[400];
		startPosition = event[xy];
	};

	return (
		<StyledBox
			className={`${classes.root} ${horizontal ? classes.horiz : classes.vert}`}
			onPointerDown={onPointerDown}
		>
			<Icon className={classes.icon} />
		</StyledBox>
	);
};

export default ResizeHandle;